import React, { useMemo, useState } from "react";
import {
    Card,
    Typography,
    Upload,
    Button,
    Select,
    Table,
    Space,
    message,
    Divider,
    Tag,
    Input,
    Progress,
    Modal,
} from "antd";
import {
    UploadOutlined,
    CloudUploadOutlined,
    FileTextOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    serverTimestamp,
} from "firebase/firestore";
import {
    ref,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import { db, strage } from "./FirebaseConfig";

const { Title, Text } = Typography;

function parseFlexibleDateToMillis(dateString) {
    if (!dateString || typeof dateString !== "string") return Date.now();

    const s = dateString.trim();
    const iso = Date.parse(s);
    if (!Number.isNaN(iso)) return iso;

    const m = s.match(
        /^(\d{2,4})[\/\-](\d{1,2})[\/\-](\d{1,2})\s+(\d{1,2}):(\d{2}):(\d{2})$/
    );

    if (m) {
        let year = Number(m[1]);
        const month = Number(m[2]);
        const day = Number(m[3]);
        const hour = Number(m[4]);
        const min = Number(m[5]);
        const sec = Number(m[6]);

        if (year < 100) year += 2000;

        return new Date(year, month - 1, day, hour, min, sec).getTime();
    }

    return Date.now();
}

function normalizeTitle(rawTitle, idClass) {
    const t = String(rawTitle || "").trim().toLowerCase();
    if (t === "open") return "open";
    if (t === "close" || t === "closed") return "close";
    return Number(idClass) === 0 ? "open" : "close";
}

function normalizeResult(result, index = 0) {
    const x = Number(result?.x ?? result?.location?.left ?? 0);
    const y = Number(result?.y ?? result?.location?.top ?? 0);

    const width =
        Number(result?.width) ||
        Math.max(
            0,
            Number(result?.location?.right ?? 0) - Number(result?.location?.left ?? 0)
        );

    const height =
        Number(result?.height) ||
        Math.max(
            0,
            Number(result?.location?.bottom ?? 0) - Number(result?.location?.top ?? 0)
        );

    const left = Number(result?.location?.left ?? x);
    const top = Number(result?.location?.top ?? y);
    const right = Number(result?.location?.right ?? left + width);
    const bottom = Number(result?.location?.bottom ?? top + height);
    const idClass = Number(result?.idClass ?? result?.classId ?? result?.class_id ?? 0);
    const title = normalizeTitle(result?.title ?? result?.label, idClass);

    return {
        aperture: Number(result?.aperture ?? 0),
        area: Number(result?.area ?? 0),
        axisLong: Number(result?.axisLong ?? result?.axis_long ?? 0),
        axisShort: Number(result?.axisShort ?? result?.axis_short ?? 0),
        confidence: Number(result?.confidence ?? result?.score ?? 0),
        id: String(result?.id ?? index),
        idClass,
        location: {
            top,
            left,
            right,
            bottom,
            scoreThreshold: Number(result?.scoreThreshold ?? 0.3),
            stability: Number(result?.stability ?? 0.1),
            status: result?.status ?? "CalcAperture",
            title,
        },
        scoreThreshold: Number(result?.scoreThreshold ?? 0.3),
        stability: Number(result?.stability ?? 0.1),
        status: result?.status ?? "CalcAperture",
        title,
    };
}

function normalizeItem(item) {
    const results = Array.isArray(item?.results)
        ? item.results.map((r, i) => normalizeResult(r, i))
        : Array.isArray(item?.detections)
            ? item.detections.map((r, i) => normalizeResult(r, i))
            : [];

    const apertures = results
        .map((r) => Number(r.aperture))
        .filter((v) => Number.isFinite(v) && v > 0);

    const avgAperture =
        item?.aperture !== undefined && item?.aperture !== null && item?.aperture !== ""
            ? Number(item.aperture)
            : apertures.length > 0
                ? apertures.reduce((a, b) => a + b, 0) / apertures.length
                : 0;

    return {
        imageName: String(item?.imageName || item?.photopath || "").trim(),
        photopath: String(item?.photopath || item?.imageName || "").trim(),
        cultiverName: String(item?.cultiverName || item?.variety || "").trim(),
        date: String(item?.date || "").trim(),
        location: String(item?.location || "").trim(),
        freeText: String(item?.freeText || "").trim(),
        important: String(item?.important || "").trim(),
        humidity: item?.humidity ?? null,
        temperature: item?.temperature ?? null,
        aperture: avgAperture > 0 ? avgAperture.toFixed(2) : "",
        yoloxModel: String(item?.yoloxModel || item?.modelName || "").trim(),
        segmentationModel: String(item?.segmentationModel || item?.segModel || "").trim(),
        timestamp: Number(item?.timestamp ?? parseFlexibleDateToMillis(item?.date)),
        wether: String(item?.wether || "").trim(),
        results,
    };
}

function normalizeImageKey(name) {
    return String(name || "").trim().toLowerCase();
}

function stripBom(text) {
    return text.replace(/^\uFEFF/, "");
}

function parseCsvLine(line) {
    const out = [];
    let cur = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];

        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                cur += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (ch === "," && !inQuotes) {
            out.push(cur);
            cur = "";
            continue;
        }

        cur += ch;
    }

    out.push(cur);
    return out;
}

function parseCsvText(text) {
    const clean = stripBom(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = clean.split("\n").filter((line) => line.trim() !== "");
    if (lines.length === 0) return [];

    const headers = parseCsvLine(lines[0]).map((h) => h.trim());

    return lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row = {};
        headers.forEach((header, idx) => {
            row[header] = values[idx] ?? "";
        });
        return row;
    });
}

function buildCsvMetaMap(rows) {
  const map = new Map();

  for (const row of rows) {
    const imageName = String(row.ImageName || row.imageName || "").trim();
    if (!imageName) continue;

    map.set(normalizeImageKey(imageName), {
      variety: String(row.Variety || row.variety || "").trim(),
      location: String(row.Location || row.location || "").trim(),
      important: String(row.Importance || row.Important || row.important || "").trim(),
      notes: String(row.Notes || row.notes || "").trim(),
      date: String(row.Date || row.date || "").trim(),
    });
  }

  return map;
}

function applyCsvMetaToItems(items, csvMetaMap) {
    if (!csvMetaMap || csvMetaMap.size === 0) return items;

    return items.map((item) => {
        const meta = csvMetaMap.get(normalizeImageKey(item.imageName));
        if (!meta) return item;

        return {
            ...item,
            cultiverName: meta.variety || item.cultiverName,
            location: meta.location || item.location,
            important: meta.important || item.important,
            freeText: meta.notes || item.freeText,
            date: meta.date || item.date,
            timestamp: meta.date ? parseFlexibleDateToMillis(meta.date) : item.timestamp,
        };
    });
}

export default function JsonImportPage() {
    const [userEmails, setUserEmails] = useState([]);
    const [selectedUserEmail, setSelectedUserEmail] = useState("");
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [jsonFileName, setJsonFileName] = useState("");
    const [csvFileName, setCsvFileName] = useState("");
    const [jsonItems, setJsonItems] = useState([]);
    const [csvMetaMap, setCsvMetaMap] = useState(new Map());

    const [imageFiles, setImageFiles] = useState([]);
    const [cultiverOverride, setCultiverOverride] = useState("");

    const [uploading, setUploading] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [progressText, setProgressText] = useState("");

    const [conflicts, setConflicts] = useState([]);
    const [failedItems, setFailedItems] = useState([]);
    const [importSummary, setImportSummary] = useState(null);

    const imageMap = useMemo(() => {
        const map = new Map();
        for (const file of imageFiles) {
            map.set(normalizeImageKey(file.name), file);
        }
        return map;
    }, [imageFiles]);

    const mergedItems = useMemo(() => {
        return applyCsvMetaToItems(jsonItems, csvMetaMap);
    }, [jsonItems, csvMetaMap]);

    const rows = useMemo(() => {
        return mergedItems.map((item, index) => {
            const matched = imageMap.has(normalizeImageKey(item.imageName));
            return {
                key: `${item.imageName}_${index}`,
                no: index + 1,
                imageName: item.imageName,
                cultiverName: item.cultiverName,
                location: item.location,
                important: item.important,
                freeText: item.freeText,
                date: item.date,
                resultsCount: item.results?.length || 0,
                matched,
            };
        });
    }, [mergedItems, imageMap]);

    const matchedCount = rows.filter((r) => r.matched).length;
    const unmatchedJsonCount = rows.filter((r) => !r.matched).length;

    const fetchUserEmails = async () => {
        setLoadingUsers(true);
        try {
            const snapshot = await getDocs(collection(db, "users"));
            const emails = snapshot.docs
                .map((snap) => snap.data()?.email || "")
                .filter((email) => typeof email === "string" && email.includes("@"))
                .sort((a, b) => a.localeCompare(b, "ja"));

            setUserEmails(emails);
            message.success("ユーザー一覧を取得しました");
        } catch (e) {
            console.error("ユーザー一覧取得エラー:", e);
            message.error("ユーザー一覧の取得に失敗しました");
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleJsonBeforeUpload = async (file) => {
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);

            const items = Array.isArray(parsed?.items)
                ? parsed.items.map(normalizeItem)
                : Array.isArray(parsed)
                    ? parsed.map(normalizeItem)
                    : [];

            if (items.length === 0) {
                message.error("JSON内に items が見つかりませんでした");
                return Upload.LIST_IGNORE;
            }

            setJsonItems(items);
            setJsonFileName(file.name);
            setImportSummary(null);
            setConflicts([]);
            setFailedItems([]);
            message.success(`JSONを読み込みました: ${file.name}`);
        } catch (e) {
            console.error(e);
            message.error("JSONの読み込みに失敗しました");
        }

        return Upload.LIST_IGNORE;
    };

    function decodeCsvArrayBuffer(buffer) {
        const uint8 = new Uint8Array(buffer);

        const utf8Text = new TextDecoder("utf-8").decode(uint8);

        // 文字化けっぽい置換文字が多いなら Shift_JIS を試す
        const badCharCount = (utf8Text.match(/�/g) || []).length;

        if (badCharCount > 0) {
            try {
                return new TextDecoder("shift-jis").decode(uint8);
            } catch (e) {
                console.warn("shift-jis decode failed, fallback to utf-8", e);
                return utf8Text;
            }
        }

        return utf8Text;
    }

    const handleCsvBeforeUpload = async (file) => {
        try {
            const buffer = await file.arrayBuffer();
            const text = decodeCsvArrayBuffer(buffer);

            const rows = parseCsvText(text);
            const metaMap = buildCsvMetaMap(rows);

            setCsvMetaMap(metaMap);
            setCsvFileName(file.name);

            message.success(`CSVを読み込みました: ${file.name}`);
        } catch (e) {
            console.error(e);
            message.error("CSVの読み込みに失敗しました");
        }

        return Upload.LIST_IGNORE;
    };

    const handleImagesBeforeUpload = (_file, fileList) => {
        const valid = fileList.filter((f) =>
            /\.(jpg|jpeg|png|bmp|tif|tiff)$/i.test(f.name)
        );
        setImageFiles(valid);
        return Upload.LIST_IGNORE;
    };

    const checkExistingDocs = async () => {
        if (!selectedUserEmail) {
            message.warning("保存先ユーザーを選択してください");
            return;
        }
        if (!mergedItems.length) {
            message.warning("JSONを読み込んでください");
            return;
        }

        try {
            const found = [];

            for (const item of mergedItems) {
                if (!item.imageName) continue;
                const docRef = doc(db, selectedUserEmail, item.imageName);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    found.push(item.imageName);
                }
            }

            setConflicts(found);

            if (found.length > 0) {
                message.warning(`既存データ ${found.length} 件あります`);
            } else {
                message.success("既存データの重複はありません");
            }
        } catch (e) {
            console.error(e);
            message.error("既存データ確認に失敗しました");
        }
    };

    const importToFirebase = async () => {
        if (!selectedUserEmail) {
            message.warning("保存先ユーザーを選択してください");
            return;
        }

        if (!mergedItems.length) {
            message.warning("JSONを読み込んでください");
            return;
        }

        if (conflicts.length > 0) {
            const ok = await new Promise((resolve) => {
                Modal.confirm({
                    title: "既存データがあります",
                    content: `${conflicts.length} 件の既存ドキュメントがあります。上書きして続行しますか？`,
                    okText: "上書きして続行",
                    cancelText: "キャンセル",
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });

            if (!ok) return;
        }

        setUploading(true);
        setProgressPercent(0);
        setProgressText("インポート開始");
        setFailedItems([]);

        try {
            let firestoreSaved = 0;
            let storageUploaded = 0;
            const failures = [];

            for (let i = 0; i < mergedItems.length; i++) {
                const item = mergedItems[i];
                const pct = Math.round(((i + 1) / Math.max(1, mergedItems.length)) * 100);
                setProgressPercent(pct);
                setProgressText(`保存中... ${i + 1}/${mergedItems.length} ${item.imageName}`);

                try {
                    if (!item.imageName) {
                        failures.push({ imageName: "(imageNameなし)", reason: "imageName が空です" });
                        continue;
                    }

                    const finalCultiverName = String(
                        (cultiverOverride || item.cultiverName || "").trim()
                    );

                    const matchedFile = imageMap.get(normalizeImageKey(item.imageName));

                    let storagePath = "";
                    let downloadURL = "";

                    if (matchedFile && finalCultiverName) {
                        storagePath = `${selectedUserEmail}/${finalCultiverName}/${matchedFile.name}`;
                        const storageRef = ref(strage, storagePath);
                        await uploadBytes(storageRef, matchedFile);
                        downloadURL = await getDownloadURL(storageRef);
                        storageUploaded += 1;
                    }

                    const docRef = doc(db, selectedUserEmail, item.imageName);

                    await setDoc(
                        docRef,
                        {
                            aperture: item.aperture ?? "",
                            cultiverName: finalCultiverName,
                            date: item.date ?? "",
                            freeText: item.freeText ?? "",
                            humidity: item.humidity ?? null,
                            important: item.important ?? "",
                            location: item.location ?? "",
                            photopath: item.imageName,
                            results: item.results ?? [],
                            temperature: item.temperature ?? null,
                            timestamp: item.timestamp ?? Date.now(),
                            userName: selectedUserEmail,
                            wether: item.wether ?? "",
                            yoloxModel: item.yoloxModel ?? "",
                            segmentationModel: item.segmentationModel ?? "",
                            storagePath,
                            downloadURL,
                            importSource: "pc-json",
                            importJsonFileName: jsonFileName,
                            importCsvFileName: csvFileName || "",
                            importedAt: serverTimestamp(),
                        },
                        { merge: true }
                    );

                    firestoreSaved += 1;
                } catch (e) {
                    console.error("import item failed:", item.imageName, e);
                    failures.push({
                        imageName: item.imageName,
                        reason: e?.message || "unknown error",
                    });
                }
            }

            setFailedItems(failures);
            setImportSummary({
                firestoreSaved,
                storageUploaded,
                failedCount: failures.length,
            });

            if (failures.length > 0) {
                message.warning(
                    `インポート完了: Firestore ${firestoreSaved}件 / Storage ${storageUploaded}件 / 失敗 ${failures.length}件`
                );
            } else {
                message.success(
                    `インポート完了: Firestore ${firestoreSaved}件 / Storage ${storageUploaded}件`
                );
            }
        } catch (e) {
            console.error(e);
            message.error("インポートに失敗しました");
        } finally {
            setUploading(false);
            setProgressText("完了");
        }
    };

    const columns = [
        {
            title: "No",
            dataIndex: "no",
            width: 60,
        },
        {
            title: "画像名",
            dataIndex: "imageName",
        },
        {
            title: "品種",
            dataIndex: "cultiverName",
            width: 140,
        },
        {
            title: "Location",
            dataIndex: "location",
            width: 140,
        },
        {
            title: "Important",
            dataIndex: "important",
            width: 110,
        },
        {
            title: "Notes",
            dataIndex: "freeText",
            width: 180,
            ellipsis: true,
        },
        {
            title: "日時",
            dataIndex: "date",
            width: 180,
        },
        {
            title: "一致",
            dataIndex: "matched",
            width: 90,
            render: (matched) =>
                matched ? <Tag color="green">一致</Tag> : <Tag color="red">未一致</Tag>,
        },
    ];

    return (
        <div style={{ maxWidth: 1380, margin: "0 auto", padding: 24 }}>
            <Title level={2}>PC解析 JSON / CSV インポート</Title>
            <Text type="secondary">
                JSON と CSV を読み込み、ImageName で突合して、Firestore と Storage に保存します。
            </Text>

            <Divider />

            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Card title="1. 保存先ユーザー">
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchUserEmails}
                            loading={loadingUsers}
                        >
                            ユーザー一覧を取得
                        </Button>

                        <Select
                            showSearch
                            allowClear
                            style={{ minWidth: 360 }}
                            placeholder="保存先ユーザーを選択"
                            value={selectedUserEmail || undefined}
                            onChange={(value) => setSelectedUserEmail(value || "")}
                            options={userEmails.map((email) => ({
                                label: email,
                                value: email,
                            }))}
                            filterOption={(input, option) =>
                                String(option?.label || "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        />

                        <Input
                            style={{ width: 260 }}
                            placeholder="品種名を上書きしたい場合"
                            value={cultiverOverride}
                            onChange={(e) => setCultiverOverride(e.target.value)}
                        />
                    </Space>
                </Card>

                <Card title="2. JSON読み込み">
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Upload
                            accept=".json,application/json"
                            beforeUpload={handleJsonBeforeUpload}
                            showUploadList={false}
                            maxCount={1}
                        >
                            <Button icon={<FileTextOutlined />}>results.json を選択</Button>
                        </Upload>

                        <Text>JSON: {jsonFileName || "未選択"}</Text>
                        <Text>件数: {jsonItems.length}</Text>
                    </Space>
                </Card>

                <Card title="3. CSV読み込み（Variety / Location / Important / Notes）">
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Upload
                            accept=".csv,text/csv"
                            beforeUpload={handleCsvBeforeUpload}
                            showUploadList={false}
                            maxCount={1}
                        >
                            <Button icon={<FileTextOutlined />}>aperture CSV を選択</Button>
                        </Upload>

                        <Text>CSV: {csvFileName || "未選択"}</Text>
                        <Text>CSV突合件数: {csvMetaMap.size}</Text>
                    </Space>
                </Card>

                <Card title="4. 画像読み込み">
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Upload
                            multiple
                            beforeUpload={handleImagesBeforeUpload}
                            showUploadList={false}
                            accept=".jpg,.jpeg,.png,.bmp,.tif,.tiff"
                        >
                            <Button icon={<UploadOutlined />}>画像をまとめて選択</Button>
                        </Upload>

                        <Text>選択画像数: {imageFiles.length}</Text>
                        <Text>一致件数: {matchedCount}</Text>
                        <Text type={unmatchedJsonCount > 0 ? "danger" : "secondary"}>
                            JSONのみ: {unmatchedJsonCount}
                        </Text>
                    </Space>
                </Card>

                <Card title="5. 突合確認">
                    <Table
                        rowKey="key"
                        columns={columns}
                        dataSource={rows}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        scroll={{ x: 1200 }}
                    />
                </Card>

                <Card title="6. 保存前チェック">
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Button onClick={checkExistingDocs} disabled={!selectedUserEmail || !mergedItems.length}>
                            既存データ確認
                        </Button>

                        <Text>既存ドキュメント: {conflicts.length}</Text>

                        {conflicts.length > 0 && (
                            <div style={{ maxHeight: 160, overflow: "auto", border: "1px solid #eee", padding: 8 }}>
                                {conflicts.map((name) => (
                                    <div key={name}>{name}</div>
                                ))}
                            </div>
                        )}
                    </Space>
                </Card>

                <Card title="7. インポート実行">
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Button
                            type="primary"
                            icon={<CloudUploadOutlined />}
                            onClick={importToFirebase}
                            loading={uploading}
                            disabled={!selectedUserEmail || !mergedItems.length}
                        >
                            Firestore / Storage に保存
                        </Button>

                        <Text type="secondary">
                            CSV がある場合は Variety / Location / Important / Notes を JSON に上書きして保存します。
                        </Text>

                        {(uploading || progressPercent > 0) && (
                            <>
                                <Progress percent={progressPercent} />
                                <Text>{progressText}</Text>
                            </>
                        )}

                        {importSummary && (
                            <div style={{ border: "1px solid #eee", padding: 12 }}>
                                <div>Firestore保存: {importSummary.firestoreSaved}</div>
                                <div>Storage保存: {importSummary.storageUploaded}</div>
                                <div>失敗件数: {importSummary.failedCount}</div>
                            </div>
                        )}

                        {failedItems.length > 0 && (
                            <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #eee", padding: 12 }}>
                                <div style={{ fontWeight: 700, marginBottom: 8 }}>失敗一覧</div>
                                {failedItems.map((f, idx) => (
                                    <div key={`${f.imageName}_${idx}`}>
                                        {f.imageName} : {f.reason}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Space>
                </Card>
            </Space>
        </div>
    );
}